import React from "react"
import {
  type EntityCategory,
  type EntityParty,
  type TaxStatus,
  type Transaction,
  type TransactionReconciliationStatus,
} from "@digits-graphql/frontend/graphql-bearer"
import { SvgXClose } from "@digits-shared/components/SVGIcons/line/XClose.svg"
import { SvgZapFast } from "@digits-shared/components/SVGIcons/line/ZapFast.svg"
import { DigitsButton } from "@digits-shared/DesignSystem/Button"
import { AlertModal } from "@digits-shared/DesignSystem/Modals/AlertModal"
import { Snackbar } from "@digits-shared/DesignSystem/Snackbar"
import { DigitsTooltip } from "@digits-shared/DesignSystem/Tooltip"
import zIndexes from "@digits-shared/DesignSystem/zIndexes"
import useStateBoolean from "@digits-shared/hooks/useStateBoolean"
import colors from "@digits-shared/themes/colors"
import { m, type Variants } from "framer-motion"
import styled from "styled-components"
import {
  BulkActionsCategoryTypeahead,
  BulkActionsSplitCategoryTypeahead,
} from "src/frontend/components/OS/Applications/Shared/Accounting/BulkActions/BulkActionsCategoryTypeahead"
import { BulkDeleteButton } from "src/frontend/components/OS/Applications/Shared/Accounting/BulkActions/BulkDeleteButton"
import { BulkTotal } from "src/frontend/components/OS/Applications/Shared/Accounting/BulkActions/BulkTotal"
import { useHasTransactionsInClosedPeriod } from "src/frontend/components/OS/Applications/Shared/Accounting/BulkActions/hooks/useHasTransactionsInClosedPeriod"
import { useIsBulkDisabled } from "src/frontend/components/OS/Applications/Shared/Accounting/BulkActions/hooks/useIsBulkDisabled"
import { usePlaceholders } from "src/frontend/components/OS/Applications/Shared/Accounting/BulkActions/hooks/usePlaceholders"
import { PartyTypeahead } from "src/frontend/components/OS/Applications/Shared/Accounting/BulkActions/PartyTypeahead"
import { ReconciliationStatusSelect } from "src/frontend/components/OS/Applications/Shared/Accounting/BulkActions/ReconciliationStatusSelect"
import { TaxStatusSelect } from "src/frontend/components/OS/Applications/Shared/Accounting/BulkActions/TaxStatusSelect"
import {
  BULK_HEADER_HEIGHT,
  BULK_STATUS_UPDATE_LIMIT,
  BulkActionField,
} from "src/frontend/components/OS/Applications/Shared/Accounting/BulkActions/types"
import { useHasMatchButtonsPermissions } from "src/frontend/components/OS/Applications/Shared/Accounting/Matching/hooks/useCanUseMatchButtons"
import { MatchButton } from "src/frontend/components/OS/Applications/Shared/Accounting/Matching/MatchButton"
import { UnmatchButton } from "src/frontend/components/OS/Applications/Shared/Accounting/Matching/UnmatchButton"
import { type NewPartyEntity } from "src/shared/components/Typeahead/sharedTypeahead"

/*
  STYLES
*/

const Header = styled(m.div)`
  position: absolute;
  top: 0;
  z-index: ${zIndexes.stickHeaderContent};

  width: 100%;
  height: ${BULK_HEADER_HEIGHT}px;
  min-height: ${BULK_HEADER_HEIGHT}px;

  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  padding: 8px 0 16px;
  gap: 16px;

  .digits-mac & {
    padding-top: calc(16px + 22px);
    height: calc(${BULK_HEADER_HEIGHT}px + 22px);
  }
`

const HeaderBackground = styled(m.div)`
  position: fixed;
  z-index: ${zIndexes.stickHeaderContent};
  top: 0;
  left: 0;
  right: 0;

  width: 100vw;
  height: ${BULK_HEADER_HEIGHT}px;
  min-height: ${BULK_HEADER_HEIGHT}px;

  background: ${colors.secondary05};
  backdrop-filter: blur(100px);
  box-shadow: 0 0 20px 0 rgba(16, 25, 63, 0.3);
  border-bottom: 1px solid ${colors.translucentWhite60};

  .digits-mac & {
    padding-top: calc(16px + 22px);
    height: calc(${BULK_HEADER_HEIGHT}px + 22px);
  }
`

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
`

/*
  COMPONENTS
*/

const variants: Variants = {
  visible: { y: 0 },
  hidden: { y: "-100%" },
}

export interface BulkActionOnUpdateProps {
  party?: EntityParty | NewPartyEntity
  category?: EntityCategory
  reconciliationStatus?: TransactionReconciliationStatus
  taxStatus?: TaxStatus
  splitCategory?: EntityCategory
  matchedTransactions?: Transaction[]
}

interface Props {
  fields: Set<BulkActionField>
  selectedTransactions: Transaction[]
  disabled?: boolean
  onCancel: () => void
  onUpdate: (updateValues: BulkActionOnUpdateProps) => void
  className?: string
  customLabels?: {
    [K in BulkActionField]?: string
  }
  includeMatch?: boolean
  onMatch?: (transactions: Transaction[]) => void
  includeUnmatch?: boolean
  onUnmatch?: (transactions: Transaction[]) => void
}

export const BulkActionsHeader: React.FC<Props> = ({
  // Order of the fields determines the order of the fields in the UI
  fields,
  selectedTransactions,
  onCancel,
  className,
  onUpdate,
  disabled = false,
  customLabels = {},
  includeMatch = false,
  onMatch,
  includeUnmatch = false,
  onUnmatch,
}) => {
  const hasMatchButtonPermission = useHasMatchButtonsPermissions()
  const { hasQuickBooksTransactionsSelected, hasTransactionWithMultipleSplit } =
    useIsBulkDisabled(selectedTransactions)
  const hasTransactionsInClosedPeriod = useHasTransactionsInClosedPeriod(selectedTransactions)

  const {
    partyPlaceholder,
    categoryPlaceholder,
    splitCategoryPlaceholder,
    reconciliationStatusPlaceholder,
    taxStatusPlaceholder,
  } = usePlaceholders(selectedTransactions)

  const [category, setCategory] = React.useState<EntityCategory | undefined>(undefined)
  const [party, setParty] = React.useState<EntityParty | NewPartyEntity | undefined>(undefined)
  const [taxStatus, setTaxStatus] = React.useState<TaxStatus | undefined>(undefined)
  const [reconciliationStatus, setReconciliationStatus] = React.useState<
    TransactionReconciliationStatus | undefined
  >(undefined)
  const [splitCategory, setSplitCategory] = React.useState<EntityCategory | undefined>(undefined)
  // Set split category to undefined if there are transactions with multiple split categories
  useResetSplitCategory(hasTransactionWithMultipleSplit, setSplitCategory)

  const {
    value: isClosedPeriodWarningVisible,
    setTrue: showClosedPeriodWarning,
    setFalse: hideClosedPeriodWarning,
  } = useStateBoolean(false)

  const applyButtonDisabled = React.useMemo(
    () =>
      (!category && !party && !reconciliationStatus && !taxStatus && !splitCategory) ||
      disabled ||
      ((category || party) && hasQuickBooksTransactionsSelected) ||
      (hasTransactionWithMultipleSplit && Boolean(splitCategory)),
    [
      category,
      party,
      reconciliationStatus,
      taxStatus,
      splitCategory,
      disabled,
      hasQuickBooksTransactionsSelected,
      hasTransactionWithMultipleSplit,
    ]
  )

  const bulkUpdateTransactions = React.useCallback(() => {
    hideClosedPeriodWarning()
    onUpdate({
      category,
      party,
      reconciliationStatus,
      taxStatus,
      splitCategory,
    })
  }, [
    hideClosedPeriodWarning,
    onUpdate,
    category,
    party,
    reconciliationStatus,
    taxStatus,
    splitCategory,
  ])

  const onApplyClick = React.useCallback(() => {
    // Only category and party modifications impact closed period transactions
    if (hasTransactionsInClosedPeriod && (category || party || splitCategory)) {
      showClosedPeriodWarning()
    } else {
      bulkUpdateTransactions()
    }
  }, [
    hasTransactionsInClosedPeriod,
    category,
    party,
    splitCategory,
    showClosedPeriodWarning,
    bulkUpdateTransactions,
  ])

  return (
    <>
      <HeaderBackground
        variants={variants}
        initial="hidden"
        animate="visible"
        exit="hidden"
        transition={{ duration: 0.2 }}
      />

      <Header
        variants={variants}
        initial="hidden"
        animate="visible"
        exit="hidden"
        transition={{ duration: 0.2 }}
        className={className}
      >
        {Array.from(fields).map((field) => {
          switch (field) {
            case BulkActionField.Party:
              return (
                <DigitsTooltip
                  content="Transactions imported from QuickBooks cannot be edited"
                  disabled={!hasQuickBooksTransactionsSelected}
                  key={field}
                >
                  <PartyTypeahead
                    party={party}
                    setParty={setParty}
                    placeholder={partyPlaceholder}
                    disabled={hasQuickBooksTransactionsSelected || disabled}
                    customLabel={customLabels[BulkActionField.Party]}
                  />
                </DigitsTooltip>
              )
            case BulkActionField.Category:
              return (
                <DigitsTooltip
                  content="Transactions imported from QuickBooks cannot be edited"
                  disabled={!hasQuickBooksTransactionsSelected}
                  key={field}
                >
                  <BulkActionsCategoryTypeahead
                    category={category}
                    setCategory={setCategory}
                    placeholder={categoryPlaceholder}
                    disabled={hasQuickBooksTransactionsSelected || disabled}
                    customLabel={customLabels[BulkActionField.Category]}
                  />
                </DigitsTooltip>
              )
            case BulkActionField.SplitCategory:
              return (
                <DigitsTooltip
                  content="Transactions imported from QuickBooks cannot be edited"
                  disabled={!hasQuickBooksTransactionsSelected}
                  key={field}
                >
                  <BulkActionsSplitCategoryTypeahead
                    category={splitCategory}
                    setCategory={setSplitCategory}
                    placeholder={splitCategoryPlaceholder}
                    disabled={
                      hasQuickBooksTransactionsSelected ||
                      disabled ||
                      hasTransactionWithMultipleSplit
                    }
                    hasTransactionWithMultipleSplit={hasTransactionWithMultipleSplit}
                    customLabel={customLabels[BulkActionField.SplitCategory]}
                  />
                </DigitsTooltip>
              )
            case BulkActionField.ReconciliationStatus:
              return (
                <ReconciliationStatusSelect
                  key={field}
                  status={reconciliationStatus}
                  setStatus={setReconciliationStatus}
                  placeholder={reconciliationStatusPlaceholder}
                  disabled={disabled}
                  customLabel={customLabels[BulkActionField.ReconciliationStatus]}
                />
              )
            case BulkActionField.TaxStatus:
              return (
                <TaxStatusSelect
                  key={field}
                  status={taxStatus}
                  setStatus={setTaxStatus}
                  placeholder={taxStatusPlaceholder}
                  disabled={disabled}
                  customLabel={customLabels[BulkActionField.TaxStatus]}
                />
              )
            default:
              return null
          }
        })}

        {includeMatch && hasMatchButtonPermission && (
          <MatchButton
            transactions={selectedTransactions}
            onMatch={onMatch}
            $variant="ghost-dark"
            size="medium"
          />
        )}

        {includeUnmatch && hasMatchButtonPermission && (
          <UnmatchButton
            transactions={selectedTransactions}
            onUnmatch={onUnmatch}
            $variant="ghost-dark"
            size="medium"
          />
        )}

        <BulkDeleteButton transactions={selectedTransactions} disabled={disabled} />

        <ButtonContainer>
          <BulkTotal transactions={selectedTransactions} />

          <DigitsButton onClick={onCancel} $variant="secondary-dark" disabled={disabled} $circle>
            <SvgXClose />
          </DigitsButton>

          <DigitsButton disabled={applyButtonDisabled} onClick={onApplyClick}>
            <SvgZapFast />
            Apply to{" "}
            {selectedTransactions.length > BULK_STATUS_UPDATE_LIMIT
              ? BULK_STATUS_UPDATE_LIMIT
              : selectedTransactions.length}
          </DigitsButton>
        </ButtonContainer>

        {isClosedPeriodWarningVisible && (
          <AlertModal
            headerLabel="Editing transactions from a Closed Period"
            primaryButtonLabel="Proceed"
            primaryButtonClick={bulkUpdateTransactions}
            primaryButtonVariant="error"
            secondaryButtonLabel="Cancel"
            secondaryButtonClick={hideClosedPeriodWarning}
          >
            Some of the transactions relate to an already closed period. Proceeding with this action
            will impact closed period balances. <br /> Are you sure you would like to proceed?
          </AlertModal>
        )}
      </Header>

      {(party || category) && hasQuickBooksTransactionsSelected && (
        <Snackbar
          message="Transactions cannot be edited or deleted"
          description={
            <div>
              Transactions imported from <strong>QuickBooks</strong> cannot be edited or deleted
            </div>
          }
          showClose={false}
        />
      )}

      {selectedTransactions.length > BULK_STATUS_UPDATE_LIMIT && (
        <Snackbar
          message="Bulk actions limit reached"
          description={`You can only edit up to ${BULK_STATUS_UPDATE_LIMIT} transactions at a time. Please complete this batch and repeat the action for the remaining transactions.`}
        />
      )}
    </>
  )
}

const useResetSplitCategory = (
  hasTransactionWithMultipleSplit: boolean,
  setSplitCategory: (c: EntityCategory | undefined) => void
) => {
  React.useEffect(() => {
    if (hasTransactionWithMultipleSplit) {
      setSplitCategory(undefined)
    }
  }, [hasTransactionWithMultipleSplit, setSplitCategory])
}
